import { useCallback, useEffect, useRef } from 'react';
import type { MessageSummary } from '@/api';
import { usePreferences } from '@/app/preferences';
import { isKeptUnread, releaseKeptUnread } from '@/routes/mail/useMarkReadAfter';

/**
 * "Mark as read after N seconds" for a conversation (plan/16 §8): the
 * `useMarkReadAfter` rules, applied to the messages that are EXPANDED, and
 * batched — one call with every expanded unread message, so the mail view
 * sends one request and patches once.
 *
 * - **Off** — nothing is marked. **0 s** — marked as soon as they are on
 *   screen. **N s** (clamped to 0–60) — marked if they are still expanded in
 *   the same opening after N seconds; leaving the conversation cancels it.
 * - A collapsed message is not read, and is not marked: collapsing an unread
 *   message before the countdown ends drops it from the batch.
 * - Each message is marked at most once per opening. A message that was
 *   already read when it was first seen, or that the reader marked unread
 *   (`cancel()` from the pane's own button, or `keepUnread()` from the mail
 *   view's Shift+U and row menu), stays as the reader left it.
 *
 * `openingKey` names one opening — account and opened message — and resets
 * all of the above when it changes. `expanded` must be the messages of the
 * conversation actually shown, never placeholder data from the previous one.
 */
export function useConversationMarkRead(
  openingKey: string | undefined,
  expanded: readonly MessageSummary[] | undefined,
  onMarkRead: ((messages: MessageSummary[]) => void) | undefined,
): { cancel: () => void } {
  const { preferences } = usePreferences();
  const { markReadEnabled, markReadDelaySeconds } = preferences;

  /** Ids marked, or deliberately left alone, in this opening. */
  const settled = useRef(new Set<string>());
  /** Every id seen in this opening, so their "kept unread" notes are dropped with it. */
  const touched = useRef(new Set<string>());
  const onMarkReadRef = useRef(onMarkRead);
  useEffect(() => {
    onMarkReadRef.current = onMarkRead;
  });
  const expandedRef = useRef(expanded);
  useEffect(() => {
    expandedRef.current = expanded;
  });

  useEffect(() => {
    if (openingKey === undefined) return;
    const settledNow = settled.current;
    const touchedNow = touched.current;
    return () => {
      settledNow.clear();
      for (const id of touchedNow) releaseKeptUnread(id);
      touchedNow.clear();
    };
  }, [openingKey]);

  /** What would be marked now: expanded, unread, not settled, not kept unread. */
  const pendingOf = useCallback((list: readonly MessageSummary[] | undefined): MessageSummary[] => {
    const pending: MessageSummary[] = [];
    for (const message of list ?? []) {
      touched.current.add(message.id);
      if (settled.current.has(message.id)) continue;
      // Already read when first seen: a later "unread" is the reader's choice.
      if (message.flags.includes('seen')) {
        settled.current.add(message.id);
        continue;
      }
      if (isKeptUnread(message.id)) continue;
      pending.push(message);
    }
    return pending;
  }, []);

  // The countdown restarts when the set of expanded unread messages changes;
  // what is settled is decided inside the effect, not during render.
  const pendingKey = (expanded ?? [])
    .filter((message) => !message.flags.includes('seen'))
    .map((message) => message.id)
    .join('\n');

  useEffect(() => {
    if (openingKey === undefined || !markReadEnabled || onMarkReadRef.current === undefined) return;
    if (pendingOf(expandedRef.current).length === 0) return;
    const mark = () => {
      const batch = pendingOf(expandedRef.current);
      if (batch.length === 0) return;
      for (const message of batch) settled.current.add(message.id);
      onMarkReadRef.current?.(batch);
    };
    const delay = Math.min(60, Math.max(0, markReadDelaySeconds));
    if (delay === 0) {
      mark();
      return;
    }
    const timer = window.setTimeout(mark, delay * 1000);
    return () => window.clearTimeout(timer);
  }, [openingKey, pendingKey, markReadEnabled, markReadDelaySeconds, pendingOf]);

  /** The reader pressed Mark unread: nothing expanded now is marked in this opening. */
  const cancel = useCallback(() => {
    for (const message of expandedRef.current ?? []) settled.current.add(message.id);
  }, []);

  return { cancel };
}
